import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import type { Track, TrackSample } from '@/world/Track';
import type { TrackTheme } from '@/world/Theme';
import type { City } from '@/config/cities';
import { Rng } from '@/core/Rng';
import { forwardX, forwardZ } from '@/math/orientation';
import { buildFinishApron, type FinishApronKind } from '@/world/TrackMesh';
import {
  BOAT_COLORS,
  DAKAR_SAND,
  LENGA,
  MORAINE,
  NAPOLI_TUFA,
  PANTS_COLORS,
  RIO_JUNGLE,
  SHIRT_COLORS,
  SKIN_TONES,
} from './palettes';
import { STAND_LAYOUT, STAND_SIDES } from './finishClear';
import { liftOntoHills } from './hills';
import { buildFinishGoal } from './finishGoal';
import { resolveWaterCfg, waterShorelines } from './water';
import type { PersonInst, SceneryArrays } from './types';

/** shared-2b: how far inboard of the water's edge a pennant pole must stand to
 *  count as planted on dry land. A pole is 0.14 thick and carries no base, so
 *  a metre of bank is the least that reads as ground rather than as a mast
 *  rising out of the lake. Poles that fail are withheld (see below). */
export const PENNANT_DRY_MARGIN = 1.2;

/** The pennant run's pole offsets from the finish line, and its lateral.
 *  Accumulated exactly as the original `for (along = 5; along <= 48;
 *  along += 8.6)` did — a literal list rounds 30.799999999999997 to 30.8 and
 *  would move that mast by 3e-15, i.e. change shipped geometry for nothing. */
export const PENNANT_ALONGS: readonly number[] = (() => {
  const out: number[] = [];
  for (let along = 5; along <= 48; along += 8.6) out.push(along);
  return out;
})();
export const PENNANT_LAT = 2.1; // outward from the road half-width

/**
 * shared-2b — WHICH PENNANT POLES A SIDE ACTUALLY GETS.
 *
 * The run used to be planted down BOTH rollout verges unconditionally at
 * hw + PENNANT_LAT — but three cities' finish water comes all the way in to
 * hw + 1 (Patagonia's meltwater, the Thames, Tokyo Bay), so five of the twelve
 * poles stood in OPEN WATER with no base and no waterline, in frame for the
 * ~2 s coast after the line (measured, both seeds, the same five in all three
 * cities plus Lagos at the margin). This is the finish plaza's existing
 * edge-reach doctrine — the one it already applies to hills — pointed at the
 * water instead.
 *
 * WITHHOLD, never relocate: the pole already sits as far inboard as the
 * rollout allows, so where hw + 2.1 is wet there is simply no land to pull it
 * onto (measured: the shoreline there is hw + 1).
 *
 * And the bunting RUNS — it starts at the line and follows the verge until the
 * land runs out, then stops. A per-pole test instead left an orphan mast
 * stranded out past the water (measured: 1 of 6 surviving in all four water
 * cities, both seeds), which reads worse than no line at all.
 *
 * STAND_SIDES is deliberately NOT reused as the gate: it is a coarse per-city
 * survey of the STAND band (hw+4.9…hw+17.5) and would wrongly strip Dakar's
 * and Lagos's land-side flags, whose poles measure dry.
 *
 * PURE + unit-tested (tests/world/finishPlazaWater.test.ts).
 */
export function dryPennantAlongs(
  shorelineAt: (arc: number, side: -1 | 1) => number | null,
  hw: number,
  fin: number,
  side: -1 | 1,
): number[] {
  const out: number[] = [];
  for (const along of PENNANT_ALONGS) {
    const shore = shorelineAt(fin + along, side);
    if (shore != null && hw + PENNANT_LAT >= shore - PENNANT_DRY_MARGIN) break;
    out.push(along);
  }
  return out;
}

/**
 * scenery/finishPlaza.ts — the DESIGNED ENDING every city gets (M97; GS3
 * material/structure upgrade 2026-08).
 *
 * The old finish was two white posts + a low checkered banner strung across
 * the road (it read as a barrier fence, and in the six gateway cities it stood
 * INSIDE the hero landmark). This module builds the finish moment instead:
 *
 *  - EVERY city gets the FINISH GOAL standing on the line (M157 — it
 *    replaced the DR gantry the non-gateway cities had, and stands INSIDE
 *    the gateway landmark in the other six; see scenery/finishGoal.ts);
 *  - every city gets GRANDSTANDS full of spectators on its land side(s) of
 *    the final approach + the rollout, and pennant FLAG LINES down the
 *    run-out verges — the celebration the finish coasts into;
 *  - the crowd rides the existing instanced people system (pushed into
 *    `a.people` before assembly), so the whole plaza adds only a handful of
 *    draw calls (geometry is merged per material).
 *
 * GS3 (the QA round's "plastic up close" fixes — measured in screenshots):
 *  - the stands are STEEL now: dark structure, canvas seat modules on the
 *    tiers (squab + backrest with per-seat divisions, tinted city-accent /
 *    white), tread-textured risers, closed side cheeks — the flat bright
 *    fascia slab ("school-bus yellow" / "Lego") is gone; gold survives ONLY
 *    as the glow-lip stripes (the M92 black+gold language);
 *  - canopies are thick raked roofs on FOUR corner masts with underside ribs
 *    and a gold edge lip (no more thin slab hovering on 2 sticks);
 *  - every stand drops its base to the local terrain MINIMUM and sinks a
 *    skirt below grade, so no corner hovers on a sloped verge (London);
 *  - rollout dressing hooks (ROLLOUT_DRESSING): a paved forecourt apron
 *    (TrackMesh.buildFinishApron) and a cheap backdrop band — NYC gets a
 *    lit-window skyline silhouette + tarmac apron (the finish used to dive
 *    into void), Rio a masking jungle tree line + low hills + paver forecourt
 *    (bare lawn). Other cities opt in during their city rounds.
 *
 * All layout randomness draws from its OWN seeded stream — the city's main
 * scenery rng sequence is untouched (the M36 determinism rule). The GS3
 * additions only APPEND draws after all existing ones, so the stand layout +
 * crowd of every city are byte-identical to the shipped M97 plaza.
 */

/**
 * THE FINISH FANFARE ROUND — per-city dressing for all nine endings.
 *
 * GS3 wired rollout dressing for TWO proof cities and left a comment saying the
 * rest "opt in during their city rounds". Seven never did, and the owner played
 * all nine and said so ("Patagonia's finish line is underwhelming for example.
 * Add more fans celebrating in the various environments"). Every city has an
 * entry now.
 *
 * `skyline` and `greenbelt` are the GS3 blocks, PARAMETERISED — colours and
 * palettes only, never draw counts — so NYC's skyline and Rio's tree line are
 * byte-identical to what shipped. Everything else (`ridge`, the rail crowd, the
 * banner run, braziers, the flotilla) is APPENDED after them, which is the only
 * way to add to a seeded stream without reshuffling what is already signed off.
 */
interface CityFanfare {
  /** Paved forecourt under the plaza (TrackMesh.buildFinishApron). */
  apron?: FinishApronKind;
  /** Silhouette tower band past the rollout, for finishes that face a void. */
  skyline?: { color: number; glow: number };
  /** Foliage band past the rollout, in one of the city's own instanced
   *  families (so it costs no new draw call where that family already
   *  exists). `hills` is the GS3 Rio jungle-slope pass. */
  greenbelt?: {
    list: 'trees' | 'palms' | 'pines';
    colors: readonly number[];
    hills?: readonly number[];
  };
  /** A LANDFORM band closing the horizon behind the rollout: Vesuvian
   *  foothills, Saharan dunes, Patagonian moraine. Appended, so it is safe. */
  ridge?: {
    colors: readonly number[];
    /** dome radius / height ranges */
    r: [number, number];
    h: [number, number];
    /** Lateral band (offsets from the road CENTRELINE) and along window. Keep
     *  `lat` under ~96: TrackMesh's ground apron reaches hw + 80, and a dome
     *  centred past that floats on nothing. */
    lat: [number, number];
    along: [number, number];
    /** domes per side */
    count: number;
  };
  /** Rail-crowd window overrides, in offsets from the finish line.
   *  `gap` withholds the crowd across a stretch that has no verge at all —
   *  see the Napoli note. */
  rail?: { from?: number; to?: number; gap?: [number, number] };
  /** Fire drums along the rail — the cold/desert endings. */
  braziers?: boolean;
  /** Spectator craft moored off the water side. Only for cities that already
   *  render boats, so it is free. */
  flotilla?: number;
}

const FANFARE: Record<string, CityFanfare> = {
  // The two GS3 cities keep their exact dressing; both gain the universal
  // rail crowd + banner run below.
  nyc: { apron: 'tarmac', skyline: { color: 0x151a26, glow: 0.55 } },
  rio: {
    apron: 'paver',
    greenbelt: { list: 'trees', colors: RIO_JUNGLE, hills: RIO_JUNGLE },
  },
  // London: the Tower Bridge gateway is spectacular and the finish behind it
  // was open grey nothing. A dusk City-of-London bank closes it.
  london: { apron: 'paver', skyline: { color: 0x2b3242, glow: 0.7 } },
  // Napoli: you finish INSIDE the Galleria and its far arch framed a flat
  // void. Vesuvian foothills + umbrella pines fill the one view it frames.
  napoli: {
    apron: 'paver',
    // THE GALLERIA HAS NO VERGE. Measured with a horizontal ray from hw + 2.4
    // at chest height: from along −40 to +40 the hall's masonry stands at
    // hw + 3.4 … 4.9 on BOTH sides, so the default crowd band (hw + 3.9 out)
    // put every spectator inside the wall — camera-verified by aiming at one
    // person's exact world position and seeing nothing but stone. The run is
    // withheld across the hall and EXTENDED instead, so the fans gather at the
    // two mouths of the arcade, which is where they would actually stand.
    rail: { from: -104, to: 100, gap: [-46, 46] },
    greenbelt: { list: 'pines', colors: [0x2f6a34, 0x3a7a3e, 0x2a5e30] },
    ridge: { colors: NAPOLI_TUFA, r: [24, 38], h: [17, 30], lat: [58, 94], along: [104, 190], count: 4 },
  },
  // Tokyo: the torii read, and everything past it was black. A dense lit
  // tower bank is the city's own language at night.
  tokyo: { apron: 'tarmac', skyline: { color: 0x1a2033, glow: 1.15 } },
  // Dakar: a rally bivouac on sand — no apron (paving a desert reads wrong),
  // dunes to close the horizon, fire drums on the rail.
  dakar: {
    // Broad and LOW: at r 28-46 / h 13-24 the first cut read as a row of tan
    // buns behind the arch, not as dunes. A dune's silhouette is its width.
    ridge: { colors: DAKAR_SAND, r: [34, 58], h: [8, 15], lat: [62, 94], along: [112, 202], count: 6 },
    braziers: true,
    flotilla: 4,
  },
  // Lagos: the Lekki pylon over an empty causeway. Warm blocks + palms.
  lagos: {
    apron: 'tarmac',
    skyline: { color: 0x3c4048, glow: 0.5 },
    greenbelt: { list: 'palms', colors: [0x2f8a44, 0x36a04e, 0x287a3c] },
    flotilla: 7,
  },
  // Mumbai: the Gateway arch framed a void. Deco cream blocks + palms.
  mumbai: {
    apron: 'paver',
    skyline: { color: 0x4a4438, glow: 0.6 },
    greenbelt: { list: 'palms', colors: [0x39813f, 0x2f7038, 0x429248] },
  },
  // PATAGONIA — the owner's named example, and the TOUR FINALE: the last thing
  // a player sees before the championship. It had a gantry on bare tundra.
  // Moraine ridge + a lenga/conifer belt + fire drums on the rail, and the
  // deepest rail crowd of the nine (see RAIL_CROWD.deepRows).
  patagonia: {
    apron: 'tarmac',
    ridge: { colors: MORAINE, r: [22, 40], h: [18, 34], lat: [60, 94], along: [110, 196], count: 5 },
    greenbelt: { list: 'pines', colors: LENGA },
    braziers: true,
  },
};

/**
 * THE RAIL CROWD — the fanfare's core, and the reason the finishes read empty.
 *
 * Before this round every plaza spectator was SEATED in a grandstand set back
 * at hw + 6.5 … hw + 17.5, so from the driver's seat the verge you actually
 * drive between was bare ground in all nine cities. This packs standing,
 * CHEERING fans behind a crowd barrier right against the rail, from the last
 * approach through the rollout.
 *
 * EVERY NUMBER BELOW IS MEASURED, not chosen — a down-ray probe over all nine
 * cities (seed 123) reporting the first obstruction outboard of the rail:
 *  - `inner` 3.9 clears the gantry pylon shaft (hw + 1.55 … 3.05), its base
 *    block (hw + 1.0 … 3.6) and the checkered kerb pods (hw + 0.95 … 2.05);
 *    the pennant masts at hw + 2.1 are 0.14 thick and sit inboard of it.
 *  - `shallowOuter` 5.4 is inboard of EVERY grandstand: the base skirt starts
 *    at hw + 5.7 and the canopy's front mast at hw + 6.05. So the shallow band
 *    is safe at any along, including straight through a stand's own footprint
 *    — which is exactly right, that is the standing terrace in front of it.
 *  - the DEEP band only runs where no stand pad exists and the forecourt rule
 *    has already cleared the ground: STAND_LAYOUT puts pads at along ±10.5 of
 *    −56 / −33 / +21, leaving (−22.5, +10.5) free, and FINISH_CLEAR deletes
 *    every vertical zone instance with along > −36 and |lateral| < 34. So
 *    along ∈ [−18, +8] out to hw + 13.4 is clear of zone structure BY
 *    CONSTRUCTION on any seed, not by luck of this one — the forecourt rule's
 *    corridor reaches hw + 16, so 13.4 keeps 2.6 u of margin. Surviving HILLS
 *    can still stand in that window (measured: a small Rio dome at hw + 13.5),
 *    which is why the crowd is run through liftOntoHills below: a spectator on
 *    a bank is right, a spectator buried in one is not.
 * A per-point water test and a hill-dome lift run on top of all of it.
 */
export const RAIL_CROWD = {
  inner: 3.9,
  shallowOuter: 5.4,
  deepOuter: 13.4,
  rowPitch: 1.5,
  deepFrom: -18,
  deepTo: 8,
  from: -64,
  to: 46,
  /** along-road spacing of one crowd slot (one person per row per slot). */
  step: 3.0,
  /** fraction of the standing rail crowd that celebrates (both arms up). */
  cheer: 0.72,
};

/** Half-length of a grandstand pad along the road, mirrored from the shared
 *  STAND_LAYOUT so the crowd and the clearing can never disagree. */
const STAND_HALF = STAND_LAYOUT.len / 2 + STAND_LAYOUT.margin;

/** Is this offset-from-the-line inside a grandstand's footprint? */
function onStandPad(along: number): boolean {
  return STAND_LAYOUT.alongs.some((a) => Math.abs(along - a) <= STAND_HALF);
}

/** How far out the crowd may stand at this offset, before the water test.
 *  PURE + unit-tested: the deep window must never overlap a stand pad. */
export function railReach(along: number): number {
  const deep = along >= RAIL_CROWD.deepFrom && along <= RAIL_CROWD.deepTo && !onStandPad(along);
  return deep ? RAIL_CROWD.deepOuter : RAIL_CROWD.shallowOuter;
}

function makeCanvas(w: number, h: number): [HTMLCanvasElement, CanvasRenderingContext2D] {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const x = c.getContext('2d');
  if (!x) throw new Error('finishPlaza: no 2D context');
  return [c, x];
}

function tex(c: HTMLCanvasElement): THREE.CanvasTexture {
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  t.anisotropy = 4;
  return t;
}

/** Soft triangular pennant with a coloured field (alpha outside the triangle). */
function pennantCanvas(css: string, checker: boolean): HTMLCanvasElement {
  const [c, x] = makeCanvas(64, 40);
  x.beginPath();
  x.moveTo(0, 0);
  x.lineTo(64, 20);
  x.lineTo(0, 40);
  x.closePath();
  x.save();
  x.clip();
  if (checker) {
    for (let cx = 0; cx < 4; cx++)
      for (let cy = 0; cy < 3; cy++) {
        x.fillStyle = (cx + cy) % 2 === 0 ? '#101114' : '#f2f2ee';
        x.fillRect(cx * 16, cy * 14, 16, 14);
      }
  } else {
    x.fillStyle = css;
    x.fillRect(0, 0, 64, 40);
    x.fillStyle = 'rgba(255,255,255,0.25)';
    x.fillRect(0, 0, 64, 6);
  }
  x.restore();
  return c;
}

/** Seat modules for the tier rows (GS3) — drawn in GRAYSCALE near-white so the
 *  material's colour tints it (one canvas serves the accent AND white rows).
 *  16 seats across 512px; a stand row is 16.4u long, so each seat lands at
 *  ~1u — human seat pitch. Bright top nosing = the bench edge trim. */
function seatCanvas(): HTMLCanvasElement {
  const [c, x] = makeCanvas(512, 64);
  x.fillStyle = '#2c2c2e'; // gaps between seats read as dark structure
  x.fillRect(0, 0, 512, 64);
  for (let i = 0; i < 16; i++) {
    const sx = i * 32 + 3;
    // seat shell
    x.fillStyle = '#e2e2e0';
    x.fillRect(sx, 6, 26, 52);
    // lower squab shading
    x.fillStyle = '#bdbdbb';
    x.fillRect(sx, 34, 26, 24);
    // inner seat-pan shadow
    x.fillStyle = '#a8a8a6';
    x.fillRect(sx + 3, 40, 20, 12);
    // bright nosing strip (bench edge trim)
    x.fillStyle = '#ffffff';
    x.fillRect(sx, 2, 26, 5);
  }
  return c;
}

/** Riser/step faces (GS3) — dark steel treads with panel seams + grime, drawn
 *  in DIRECT colour (no tint). Repeats twice per stand length. */
function treadCanvas(): HTMLCanvasElement {
  const [c, x] = makeCanvas(256, 64);
  x.fillStyle = '#23262c';
  x.fillRect(0, 0, 256, 64);
  // horizontal grime banding
  x.fillStyle = '#1d2026';
  x.fillRect(0, 40, 256, 24);
  x.fillStyle = '#191c21';
  x.fillRect(0, 54, 256, 10);
  // tread nose line (catches light at the step edge)
  x.fillStyle = '#3d434e';
  x.fillRect(0, 0, 256, 4);
  // vertical panel seams
  x.fillStyle = '#171a1f';
  for (let sx = 0; sx < 256; sx += 64) x.fillRect(sx, 0, 2, 64);
  // faint speckle (deterministic LCG — no rng stream involved)
  let s = 0x51ee7;
  for (let i = 0; i < 90; i++) {
    s = (s * 1664525 + 1013904223) >>> 0;
    const px = s % 256;
    const py = (s >>> 8) % 64;
    x.fillStyle = (s & 1) === 0 ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.18)';
    x.fillRect(px, py, 2, 2);
  }
  return c;
}

/** The owner's logo art (M148, public/brand/title-logo-sm.webp), loaded once
 *  for the verge banners; each banner repaints itself when it lands. No DOM
 *  image (tests, a failed load) simply keeps the drawn logotype fallback. */
let brandImg: HTMLImageElement | null = null;
let brandReady = false;
const brandWaiters: ((img: HTMLImageElement) => void)[] = [];
export function withBrandLogo(cb: (img: HTMLImageElement) => void): void {
  if (brandReady && brandImg) {
    cb(brandImg);
    return;
  }
  if (typeof Image === 'undefined') return;
  brandWaiters.push(cb);
  if (brandImg) return;
  const img = new Image();
  brandImg = img;
  img.onload = (): void => {
    brandReady = true;
    for (const w of brandWaiters.splice(0)) w(img);
  };
  img.onerror = (): void => {
    brandWaiters.length = 0;
  };
  img.src = `${import.meta.env.BASE_URL}brand/title-logo-sm.webp`;
}

/** The verge BANNER (fanfare round). Vertical cloth in the city's accent
 *  carrying the brand logo with a gold head/foot bar and a checkered hem.
 *  One canvas per city serves the whole run, so the banners are ONE draw call.
 *  Drawn on a 256×640 sheet: at 1.5 × 3.7 u that is ~170 px per metre.
 *  M148: the logo is the owner's art once it has loaded (`logo`); until then
 *  (and in tests) the chrome DRIBBLE / gold RACER logotype stands in. */
function paintBanner(c: HTMLCanvasElement, accentCss: string, logo: HTMLImageElement | null): void {
  const x = c.getContext('2d');
  if (!x) return;
  // accent field over near-black, with a soft vertical shade so flat cloth
  // does not read as a printed card
  x.fillStyle = '#0d0e12';
  x.fillRect(0, 0, 256, 640);
  const field = x.createLinearGradient(0, 0, 256, 0);
  field.addColorStop(0, '#0d0e12');
  field.addColorStop(0.18, accentCss);
  field.addColorStop(0.72, accentCss);
  field.addColorStop(1, '#0d0e12');
  x.globalAlpha = 0.82;
  x.fillStyle = field;
  x.fillRect(0, 54, 256, 532);
  x.globalAlpha = 1;
  // gold head + foot bars
  x.fillStyle = '#d8a63a';
  x.fillRect(0, 22, 256, 26);
  x.fillRect(0, 592, 256, 26);
  // checkered hem
  for (let cx = 0; cx < 8; cx++)
    for (let cy = 0; cy < 2; cy++) {
      x.fillStyle = (cx + cy) % 2 === 0 ? '#f4f4f0' : '#101114';
      x.fillRect(cx * 32, 526 + cy * 30, 32, 30);
    }
  if (logo) {
    // a soft dark pool so the art holds up on the brightest city accents
    const pool = x.createRadialGradient(128, 196, 10, 128, 196, 150);
    pool.addColorStop(0, 'rgba(8, 9, 14, 0.62)');
    pool.addColorStop(1, 'rgba(8, 9, 14, 0)');
    x.fillStyle = pool;
    x.fillRect(0, 60, 256, 280);
    const w = 240;
    const h = (w * logo.naturalHeight) / logo.naturalWidth;
    x.drawImage(logo, (256 - w) / 2, 196 - h / 2, w, h);
    return;
  }
  // logotype fallback, stacked
  x.save();
  x.translate(128, 300);
  x.textAlign = 'center';
  x.textBaseline = 'middle';
  // MEASURED: at 62px "DRIBBLE" is ~250px wide on a 256px sheet and the D and
  // E ran off the cloth in game. 42/50 keeps both words inside the field with
  // a real margin at the banner's 1.5 u width.
  const chrome = x.createLinearGradient(0, -150, 0, -100);
  chrome.addColorStop(0, '#ffffff');
  chrome.addColorStop(0.55, '#c9d0da');
  chrome.addColorStop(1, '#7d8794');
  x.font = '900 42px "Arial Black", Arial, sans-serif';
  x.fillStyle = chrome;
  x.fillText('DRIBBLE', 0, -118);
  const g = x.createLinearGradient(0, -86, 0, -34);
  g.addColorStop(0, '#ffe9a8');
  g.addColorStop(0.5, '#f2b21c');
  g.addColorStop(1, '#b47a10');
  x.font = '900 50px "Arial Black", Arial, sans-serif';
  x.fillStyle = g;
  x.fillText('RACER', 0, -60);
  x.restore();
}

/** The banner texture: painted now with the fallback, repainted with the
 *  logo art the moment it is in (the texture re-uploads itself). */
function bannerTexture(accentCss: string): THREE.CanvasTexture {
  const [c] = makeCanvas(256, 640);
  paintBanner(c, accentCss, brandReady ? brandImg : null);
  const t = tex(c);
  if (!brandReady) {
    withBrandLogo((img) => {
      paintBanner(c, accentCss, img);
      t.needsUpdate = true;
    });
  }
  return t;
}

/** Lit-window emissive grid for the NYC skyline backdrop band (GS3). Dense
 *  (12×16) so it still reads as windows when stretched over a tall box.
 *  Deterministic local LCG — the plaza rng only places the towers. */
function windowGlowCanvas(): HTMLCanvasElement {
  const [c, x] = makeCanvas(96, 128);
  x.fillStyle = '#000000';
  x.fillRect(0, 0, 96, 128);
  let s = 0xace5;
  for (let row = 0; row < 16; row++)
    for (let col = 0; col < 12; col++) {
      s = (s * 1664525 + 1013904223) >>> 0;
      if (s / 4294967296 > 0.3) continue; // ~30% of windows lit
      const warm = (s >>> 4) % 3;
      x.fillStyle = warm === 0 ? '#ffd9a0' : warm === 1 ? '#ffc27a' : '#e8e2c8';
      x.fillRect(col * 8 + 2, row * 8 + 2, 4, 5);
    }
  return c;
}

const css = (n: number): string => `#${n.toString(16).padStart(6, '0')}`;

/** Sample the track at an arc distance, extrapolating STRAIGHT past the last
 *  node (the rollout + apron continue beyond `track.length`). */
function sampleBeyond(track: Track, d: number): TrackSample {
  if (d <= track.length) return track.sampleAt(d);
  const s = track.sampleAt(track.length);
  const over = d - track.length;
  return {
    ...s,
    x: s.x + forwardX(s.dir) * over,
    z: s.z + forwardZ(s.dir) * over,
    distance: d,
  };
}

/** Merge-bucket helper: collect transformed box/plane geometries per material,
 *  emit ONE mesh per bucket. Keeps the whole plaza to ~12 draw calls.
 *  `rotZ` rolls the box about its LOCAL Z (the along-road axis after the yaw)
 *  — used for the raked canopy roofs. */
class Buckets {
  private map = new Map<THREE.Material, THREE.BufferGeometry[]>();

  add(
    mat: THREE.Material,
    geo: THREE.BufferGeometry,
    x: number,
    y: number,
    z: number,
    rotY: number,
    sx = 1,
    sy = 1,
    sz = 1,
    rotZ = 0,
  ): void {
    const g = geo.clone();
    const q = new THREE.Quaternion().setFromEuler(new THREE.Euler(0, rotY, 0));
    if (rotZ !== 0) q.multiply(new THREE.Quaternion().setFromEuler(new THREE.Euler(0, 0, rotZ)));
    const m = new THREE.Matrix4().compose(
      new THREE.Vector3(x, y, z),
      q,
      new THREE.Vector3(sx, sy, sz),
    );
    g.applyMatrix4(m);
    let list = this.map.get(mat);
    if (!list) this.map.set(mat, (list = []));
    list.push(g);
  }

  build(into: THREE.Group): void {
    for (const [mat, geos] of this.map) {
      const merged = mergeGeometries(geos, false);
      if (!merged) continue;
      for (const g of geos) g.dispose();
      into.add(new THREE.Mesh(merged, mat));
    }
  }
}

const BOX = new THREE.BoxGeometry(1, 1, 1);

/** Build the finish plaza. Pushes spectators into `a.people` (call BEFORE the
 *  instanced assembly) and returns the plaza mesh group. */
export function buildFinishPlaza(
  track: Track,
  city: City,
  theme: TrackTheme,
  a: SceneryArrays,
): THREE.Group {
  const group = new THREE.Group();
  const rng = new Rng((city.seed ^ 0x71a2a5) >>> 0);
  const hw = track.halfWidth;
  const fin = track.finishDistance;
  const accent = theme.roadLine;

  // Shared materials (per city build — a handful of allocations).
  const steel = new THREE.MeshStandardMaterial({ color: 0x191c23, roughness: 0.52, metalness: 0.55 });
  // canopy panels: a step lighter than the structure so elevated views read
  // "metal roof", not a black hole (measured Patagonia/NYC overviews)
  const roofSteel = new THREE.MeshStandardMaterial({ color: 0x2a303b, roughness: 0.45, metalness: 0.62 });
  const gold = new THREE.MeshStandardMaterial({ color: 0xd8a63a, roughness: 0.35, metalness: 0.85 });
  const goldGlow = new THREE.MeshBasicMaterial({ color: 0xf7c74a }); // the M92 glow-lip gold
  const treadMat = new THREE.MeshStandardMaterial({
    map: tex(treadCanvas()),
    roughness: 0.62,
    metalness: 0.4,
  });
  const seatTex = tex(seatCanvas());
  const seatMats = [
    new THREE.MeshStandardMaterial({ map: seatTex, color: accent, roughness: 0.6 }),
    new THREE.MeshStandardMaterial({ map: seatTex, color: 0xe8e6de, roughness: 0.6 }),
  ];
  const b = new Buckets();

  // ---- THE FINISH GOAL (M157): every city, standing on the line ---------
  // It replaces the DR gantry + checkered kerb pods (owner: "replace the
  // current arch structure just as a goal"); in the six landmark cities it
  // stands inside the gateway (Tower Bridge, the Galleria, the torii…).
  group.add(buildFinishGoal(track));

  // ---- Grandstands: the final approach + the rollout, land sides only ------
  // GS3 structure: terrain-min base, steel skirt + cheeks, tread risers, seat
  // modules (squab + backrest), dark fascia with a gold glow lip, and a thick
  // raked canopy on four corner masts with underside ribs.
  const sides = STAND_SIDES[city.id] ?? ([-1, 1] as const);
  const standAlongs = STAND_LAYOUT.alongs;
  const standLen = STAND_LAYOUT.len;
  for (const side of sides) {
    for (const along of standAlongs) {
      if (rng.next() < 0.12) continue; // the odd missing stand keeps it organic
      const s = sampleBeyond(track, fin + along);
      const faceRot = s.dir + (side < 0 ? Math.PI / 2 : -Math.PI / 2); // face the road
      const frontLat = hw + 6.5;
      const tierDepth = 1.35;
      const tierRise = 0.82;
      const tiers = 4;
      const cx = (lat: number): [number, number] => [s.x + s.rightX * side * lat, s.z + s.rightZ * side * lat];
      // The verge ground plane sits 0.5 under the road nodes (TrackMesh
      // yGround); the base is the terrain MINIMUM across the stand's length,
      // so on a sloped verge no corner can hover (measured in London).
      const base =
        Math.min(
          sampleBeyond(track, fin + along - standLen / 2).y,
          s.y,
          sampleBeyond(track, fin + along + standLen / 2).y,
        ) - 0.5;
      const floorTop = (k: number): number => base + 1.15 + k * tierRise;
      // base skirt: sinks 0.7 below grade, hides every slope gap
      {
        const midLat = frontLat + (tiers * tierDepth) / 2;
        const [x, z] = cx(midLat);
        b.add(steel, BOX, x, base + 0.3, z, s.dir, tiers * tierDepth + 1.6, 2.0, standLen + 1.0);
      }
      for (let k = 0; k < tiers; k++) {
        const lat = frontLat + 0.7 + k * tierDepth;
        const [x, z] = cx(lat);
        // riser face (tread texture) up to this tier's floor
        b.add(treadMat, BOX, x, floorTop(k) - 0.45, z, s.dir, tierDepth, 0.9, standLen);
        // seat row: squab + backrest, per-seat canvas, alternating accent/white
        const seatMat = seatMats[k % 2];
        const [qx, qz] = cx(lat - 0.22);
        b.add(seatMat, BOX, qx, floorTop(k) + 0.12, qz, s.dir, 0.8, 0.24, standLen - 0.6);
        const [rx2, rz2] = cx(lat + 0.45);
        b.add(seatMat, BOX, rx2, floorTop(k) + 0.3, rz2, s.dir, 0.1, 0.56, standLen - 0.6);
      }
      // side cheeks close the stepped silhouette (the open ends read hollow)
      for (const endOff of [-(standLen / 2 + 0.09), standLen / 2 + 0.09]) {
        const midLat = frontLat + (tiers * tierDepth) / 2 + 0.1;
        const x = s.x + s.rightX * side * midLat + forwardX(s.dir) * endOff;
        const z = s.z + s.rightZ * side * midLat + forwardZ(s.dir) * endOff;
        b.add(steel, BOX, x, base + 2.0, z, s.dir, tiers * tierDepth + 0.6, 3.4, 0.18);
      }
      // front fascia: DARK panel (the bright accent slab read as plastic —
      // measured NYC/Mumbai) with the gold glow lip on top: M92 black+gold.
      {
        const [x, z] = cx(frontLat - 0.02);
        b.add(steel, BOX, x, base + 0.85, z, s.dir, 0.2, 1.0, standLen);
        const [gx, gz] = cx(frontLat - 0.04);
        b.add(goldGlow, BOX, gx, base + 1.42, gz, s.dir, 0.16, 0.1, standLen - 0.5);
      }
      // canopy: thick raked roof on FOUR corner masts + underside ribs + a
      // gold edge lip (the old thin slab hovered on 2 sticks — measured).
      {
        const rake = 0.12; // radians — front (road) edge high, back edge low
        const rotZ = -side * rake;
        const canopyDepth = tiers * tierDepth + 2.8; // 8.2
        const canopyLat = frontLat + (tiers * tierDepth) / 2 + 0.4; // roof centre
        const frontEdgeLat = canopyLat - canopyDepth / 2;
        const roofMidY = base + 6.25;
        const edgeDrop = (canopyDepth / 2) * rake; // ±0.49 at the edges
        const frontEdgeY = roofMidY + edgeDrop;
        const backEdgeY = roofMidY - edgeDrop;
        // four corner masts
        for (const mz of [-standLen / 2 + 1, standLen / 2 - 1]) {
          const mast = (lat: number, topY: number): void => {
            const x = s.x + s.rightX * side * lat + forwardX(s.dir) * mz;
            const z = s.z + s.rightZ * side * lat + forwardZ(s.dir) * mz;
            b.add(steel, BOX, x, (base + topY) / 2, z, s.dir, 0.24, topY - base, 0.24);
          };
          mast(frontEdgeLat + 0.55, frontEdgeY - 0.12);
          mast(canopyLat + canopyDepth / 2 - 0.35, backEdgeY - 0.12);
        }
        const [cxr, czr] = cx(canopyLat);
        // main slab (thick, dark steel — a full gold top sheet read as flat
        // tan cardboard from elevated views, measured) + a gold TRIM STRIP
        // along the roof's road edge: the gantry's own black+gold recipe.
        b.add(roofSteel, BOX, cxr, roofMidY, czr, s.dir, canopyDepth, 0.3, standLen + 1.2, rotZ);
        const [tx2, tz2] = cx(frontEdgeLat + 0.45);
        b.add(gold, BOX, tx2, frontEdgeY - 0.06, tz2, s.dir, 0.7, 0.08, standLen + 1.25, rotZ);
        // underside ribs spanning the depth
        for (let ri = 0; ri < 4; ri++) {
          const off = -standLen / 2 + 2.2 + (ri * (standLen - 4.4)) / 3;
          const x = s.x + s.rightX * side * canopyLat + forwardX(s.dir) * off;
          const z = s.z + s.rightZ * side * canopyLat + forwardZ(s.dir) * off;
          b.add(steel, BOX, x, roofMidY - 0.22, z, s.dir, canopyDepth - 0.5, 0.16, 0.18, rotZ);
        }
        // front drop fascia + gold lip under the roof's road edge
        const [fx2, fz2] = cx(frontEdgeLat + 0.1);
        b.add(steel, BOX, fx2, frontEdgeY - 0.32, fz2, s.dir, 0.16, 0.44, standLen + 1.2);
        b.add(goldGlow, BOX, fx2, frontEdgeY - 0.58, fz2, s.dir, 0.12, 0.09, standLen + 0.6);
      }
      // pack the tiers with seated spectators (instanced crowd) — the rng
      // draw ORDER here is the shipped M97 sequence (layout determinism).
      for (let k = 0; k < tiers; k++) {
        const lat = frontLat + 0.7 + k * tierDepth;
        const n = 6 + rng.int(0, 3);
        for (let i = 0; i < n; i++) {
          const off = -standLen / 2 + 1.2 + ((standLen - 2.4) * (i + rng.range(0.1, 0.9))) / n;
          a.people.push({
            x: s.x + s.rightX * side * (lat - 0.18) + forwardX(s.dir) * off,
            y: floorTop(k) + 0.24,
            z: s.z + s.rightZ * side * (lat - 0.18) + forwardZ(s.dir) * off,
            rotY: faceRot + rng.range(-0.25, 0.25),
            s: rng.range(0.86, 1.0),
            shirt: rng.pick(SHIRT_COLORS),
            skin: rng.pick(SKIN_TONES),
            pants: rng.pick(PANTS_COLORS),
            sit: true,
          });
        }
      }
    }
  }

  // ---- Pennant flag lines down the rollout verges (dry verge only) ---------
  // The run walks each side until the land runs out — see dryPennantAlongs.
  const shorelineAt = waterShorelines(a.ponds, track, resolveWaterCfg(city.water));
  const pennantMats = [
    new THREE.MeshBasicMaterial({ map: tex(pennantCanvas(css(accent), false)), transparent: true, side: THREE.DoubleSide }),
    new THREE.MeshBasicMaterial({ map: tex(pennantCanvas('#f2f2ee', false)), transparent: true, side: THREE.DoubleSide }),
    new THREE.MeshBasicMaterial({ map: tex(pennantCanvas('', true)), transparent: true, side: THREE.DoubleSide }),
  ];
  const PENNANT = new THREE.PlaneGeometry(1.5, 0.95);
  let flagIdx = 0;
  for (const side of [-1, 1] as const) {
    const dryAlongs = new Set(dryPennantAlongs(shorelineAt, hw, fin, side));
    for (const along of PENNANT_ALONGS) {
      // The colour cycle advances for withheld poles too, so every surviving
      // pennant keeps exactly the colour it had before this rule existed.
      const mat = pennantMats[flagIdx++ % pennantMats.length];
      if (!dryAlongs.has(along)) continue;
      const s = sampleBeyond(track, fin + along);
      const lat = side * (hw + PENNANT_LAT);
      const x = s.x + s.rightX * lat;
      const z = s.z + s.rightZ * lat;
      b.add(steel, BOX, x, s.y + 2.1, z, s.dir, 0.14, 4.2, 0.14);
      b.add(gold, BOX, x, s.y + 4.32, z, s.dir, 0.22, 0.22, 0.22);
      // pennant flying inward over the verge (merged per colour bucket)
      b.add(
        mat,
        PENNANT,
        x - s.rightX * side * 0.95,
        s.y + 3.7,
        z - s.rightZ * side * 0.95,
        s.dir + (side < 0 ? Math.PI / 2 : -Math.PI / 2),
      );
    }
  }

  // ---- Rollout dressing (GS3): paved forecourt + backdrop band -------------
  // Appended AFTER every existing draw so cities without dressing keep a
  // byte-identical plaza rng sequence.
  const dress: CityFanfare = FANFARE[city.id] ?? {};
  if (dress.skyline) {
    // A silhouette skyline band past the gantry: dark tower boxes with sparse
    // lit windows, sunk into the ground apron, washed by the city fog — the
    // finish stops diving into void (measured NYC). COLOUR ONLY is per city;
    // the draw sequence is exactly GS3's, so NYC is byte-identical.
    const skylineMat = new THREE.MeshStandardMaterial({
      color: dress.skyline.color,
      roughness: 0.9,
      emissive: 0xffffff,
      emissiveMap: tex(windowGlowCanvas()),
      emissiveIntensity: dress.skyline.glow,
    });
    const tower = (along: number, lat: number, w: number, h: number, d: number): void => {
      const s = sampleBeyond(track, fin + along);
      const x = s.x + s.rightX * lat;
      const z = s.z + s.rightZ * lat;
      b.add(skylineMat, BOX, x, s.y - 1.5 + h / 2, z, s.dir + rng.range(-0.15, 0.15), w, h, d);
    };
    for (const side of [-1, 1] as const) {
      // near rank: flanks the rollout
      for (let i = 0; i < 5; i++) {
        const along = 96 + i * 13 + rng.range(-4, 4);
        const lat = side * rng.range(36, 78);
        tower(along, lat, rng.range(9, 16), rng.range(15, 32), rng.range(8, 13));
      }
      // far rank: taller, hazier
      for (let i = 0; i < 4; i++) {
        const along = 150 + i * 15 + rng.range(-5, 5);
        const lat = side * rng.range(26, 88);
        tower(along, lat, rng.range(11, 19), rng.range(26, 48), rng.range(9, 15));
      }
    }
    // centre-far towers silhouetting behind the ball-drop hero
    for (let i = 0; i < 3; i++) {
      tower(168 + i * 16 + rng.range(-4, 4), rng.range(-18, 18), rng.range(12, 20), rng.range(30, 50), rng.range(10, 15));
    }
  }
  const groundAt = (along: number): TrackSample => sampleBeyond(track, fin + along);
  /**
   * THE VERGE SURFACE the fanfare furniture stands on, as an offset from the
   * ROAD node height.
   *
   * MEASURED, and it cost a whole QA round: the zone ground plane sits 0.5
   * BELOW the road nodes (TrackMesh yGround), but a city with a paved forecourt
   * has the apron laid 0.12 ABOVE them (TrackMesh.buildFinishApron LIFT) across
   * exactly the band the rail crowd occupies (hw + 0.35 … hw + 20). Planting
   * the crowd at the old ground height therefore sank every spectator 0.62 u
   * into the pavement — knee-deep, and in Napoli's Galleria, where the apron
   * kerb reads as a ledge, the crowd was invisible from the road (down-ray
   * probe: the floor under the whole band is road + 0.12, all nine cities).
   *
   * Everything BEYOND the apron's reach — the greenbelt and the ridge, out at
   * lateral 46 … 128 — deliberately keeps the ground height.
   */
  const APRON_LIFT = 0.12;
  const vergeY = (s: TrackSample): number => s.y + (dress.apron ? APRON_LIFT : -0.5);
  /** Nothing may stand in water: the shoreline query is the same slab metadata
   *  the sheet is welded from (see waterShorelines), and PENNANT_DRY_MARGIN is
   *  the plaza's existing "a metre of bank or it reads as a mast rising out of
   *  the lake" rule. Used by every new placement below. */
  const dry = (along: number, side: -1 | 1, lat: number): boolean => {
    const shore = shorelineAt(fin + along, side);
    return shore == null || lat < shore - PENNANT_DRY_MARGIN;
  };

  if (dress.greenbelt) {
    // Masking tree line + (Rio only) a low hill band around the rollout
    // plateau — the bare-lawn void behind the gantry (measured Rio). Pushed
    // into the instanced arrays BEFORE assembly, so they render with the
    // city's own foliage look at near-zero cost. FANFARE ROUND: the family
    // and palette are parameters now (Napoli/Patagonia pines, Lagos/Mumbai
    // palms); Rio's own draw sequence is untouched.
    const gb = dress.greenbelt;
    const plant = (x: number, y: number, z: number, s: number, color: number): void => {
      if (gb.list === 'trees') a.trees.push({ x, y, z, s, color });
      // palms/pines are PlaceInst: they carry a yaw, so they need one extra
      // draw. Taken ONLY in this branch, i.e. never for Rio.
      else a[gb.list].push({ x, y, z, rotY: rng.range(0, Math.PI * 2), s, color });
    };
    for (const side of [-1, 1] as const) {
      for (let along = 88; along <= 172; along += 12) {
        const s = groundAt(along + rng.range(-4, 4));
        const lat = side * rng.range(46, 96);
        // The draws are ALWAYS taken and the result discarded when the spot is
        // wet — Rio's sequence (which never withholds) has to stay identical,
        // and a conditional draw would make a wet city's whole belt shift.
        const s1 = rng.range(1.3, 2.1);
        const c1 = rng.pick(gb.colors);
        if (dry(along, side, Math.abs(lat)))
          plant(s.x + s.rightX * lat, s.y - 0.5, s.z + s.rightZ * lat, s1, c1);
        // a looser second rank behind
        const s2 = groundAt(along + rng.range(-2, 6));
        const lat2 = side * rng.range(62, 110);
        const sc2 = rng.range(1.5, 2.4);
        const c2 = rng.pick(gb.colors);
        if (dry(along, side, Math.abs(lat2)))
          plant(s2.x + s2.rightX * lat2, s2.y - 0.5, s2.z + s2.rightZ * lat2, sc2, c2);
      }
      if (gb.hills) {
        for (let i = 0; i < 3; i++) {
          const s = groundAt(rng.range(118, 172));
          const lat = side * rng.range(58, 105);
          a.hills.push({
            x: s.x + s.rightX * lat,
            y: s.y - 0.5,
            z: s.z + s.rightZ * lat,
            r: rng.range(20, 32),
            h: rng.range(6, 11),
            color: rng.pick(gb.hills),
          });
        }
      }
    }
  }
  if (dress.apron) group.add(buildFinishApron(track, theme, dress.apron));

  // =========================================================================
  // THE FANFARE ROUND. Everything below is APPENDED after every GS3 draw, so
  // NYC's and Rio's shipped plazas — stands, crowd, pennants, skyline, tree
  // line — are byte-identical. New draws can only ever affect what follows.
  // =========================================================================

  // ---- Landform ridge closing the horizon behind the rollout ---------------
  if (dress.ridge) {
    const rd = dress.ridge;
    for (const side of [-1, 1] as const) {
      for (let i = 0; i < rd.count; i++) {
        const along = rng.range(rd.along[0], rd.along[1]);
        const lat = rng.range(rd.lat[0], rd.lat[1]);
        const r = rng.range(rd.r[0], rd.r[1]);
        const h = rng.range(rd.h[0], rd.h[1]);
        const color = rng.pick(rd.colors);
        // A dome REACHES `r` inward from its centre — the water test has to be
        // applied to its near edge, not to the centre, or a ridge grows out of
        // the lake (the exact defect M119 fixed for trees).
        if (!dry(along, side, lat - r)) continue;
        const s = groundAt(along);
        a.hills.push({
          x: s.x + s.rightX * side * lat,
          y: s.y - 0.5,
          z: s.z + s.rightZ * side * lat,
          r,
          h,
          color,
        });
      }
    }
  }

  // ---- THE RAIL CROWD: standing, cheering fans against the barrier ---------
  // See RAIL_CROWD for where every number comes from. Sides come from the
  // STAND_SIDES land survey (the coarse gate — a city whose finish faces open
  // water gets no crowd standing on it) and each individual slot is then tested
  // against the actual shoreline.
  const railPeople: PersonInst[] = [];
  const railFrom = dress.rail?.from ?? RAIL_CROWD.from;
  const railTo = dress.rail?.to ?? RAIL_CROWD.to;
  const railGap = dress.rail?.gap;
  const inGap = (along: number): boolean =>
    railGap !== undefined && along >= railGap[0] && along <= railGap[1];
  for (const side of sides) {
    for (let along = railFrom; along <= railTo + 1e-6; along += RAIL_CROWD.step) {
      if (inGap(along)) continue;
      const s = groundAt(along);
      const reach = railReach(along);
      for (let lat = RAIL_CROWD.inner; lat <= reach + 1e-6; lat += RAIL_CROWD.rowPitch) {
        // Back rows thin out — a solid rectangle of people reads as a wall.
        const depth = (lat - RAIL_CROWD.inner) / RAIL_CROWD.rowPitch;
        if (rng.next() > 0.94 - depth * 0.075) continue;
        const jl = lat + rng.range(-0.28, 0.28);
        const ja = along + rng.range(-0.9, 0.9);
        if (!dry(ja, side, hw + jl)) continue;
        const sa = groundAt(ja);
        railPeople.push({
          x: sa.x + sa.rightX * side * (hw + jl),
          y: vergeY(sa),
          z: sa.z + sa.rightZ * side * (hw + jl),
          // face the road, with real spread — a crowd all square-on is a fence
          rotY: s.dir + (side < 0 ? Math.PI / 2 : -Math.PI / 2) + rng.range(-0.5, 0.5),
          s: rng.range(0.9, 1.08),
          shirt: rng.pick(SHIRT_COLORS),
          skin: rng.pick(SKIN_TONES),
          pants: rng.pick(PANTS_COLORS),
          sit: false,
          cheer: rng.next() < RAIL_CROWD.cheer,
        });
      }
    }
    // MEASURED DEAD END — do not re-add a crowd barrier here. The first pass
    // ran a dark kick panel + gold glow lip at hw + 3.15 along the whole crowd
    // and it was INVISIBLE in every shot: the course's own timber guardrail
    // stands AT hw and is taller, so it occludes anything on the verge behind
    // it from the chase camera's eye height. The guardrail already IS the
    // barrier the crowd leans on; a second one is pure geometry.
  }
  // Stand the crowd ON the dome field rather than inside it (the M119 tree
  // rule, which the flags noted crowds never got). Y-only, rng-free, and the
  // hills are FINAL here: clearFinishZone and clearShortcutCorridors have both
  // already run, and this pass's own ridge domes are in the list above.
  liftOntoHills(railPeople, a.hills);
  for (const p of railPeople) a.people.push(p);

  // ---- Banner masts down the APPROACH verge + bunting between them ---------
  // Deliberately on the approach only: the pennant run above already owns the
  // rollout (alongs 5 … 48), so between them the whole verge is dressed and
  // neither ever shares a mast position with the other.
  const bannerMat = new THREE.MeshBasicMaterial({
    map: bannerTexture(css(accent)),
    side: THREE.DoubleSide,
  });
  const BANNER = new THREE.PlaneGeometry(1.5, 3.7);
  const BUNTING = new THREE.PlaneGeometry(0.5, 0.34);
  const MAST_LAT = 3.0;
  const mastAlongs: number[] = [];
  for (let along = -58; along <= -6; along += 13) mastAlongs.push(along);
  for (const side of sides) {
    const planted: { along: number; x: number; y: number; z: number; dir: number }[] = [];
    for (const along of mastAlongs) {
      if (!dry(along, side, hw + MAST_LAT)) continue;
      const s = groundAt(along);
      const lat = side * (hw + MAST_LAT);
      const x = s.x + s.rightX * lat;
      const z = s.z + s.rightZ * lat;
      const y = vergeY(s);
      b.add(steel, BOX, x, y + 3.9, z, s.dir, 0.2, 7.8, 0.2);
      b.add(gold, BOX, x, y + 7.95, z, s.dir, 0.3, 0.3, 0.3);
      // the banner hangs on the mast facing the road (never over it)
      b.add(
        bannerMat,
        BANNER,
        x - s.rightX * side * 0.12,
        y + 5.6,
        z - s.rightZ * side * 0.12,
        s.dir + (side < 0 ? Math.PI / 2 : -Math.PI / 2),
      );
      planted.push({ along, x, y, z, dir: s.dir });
    }
    // BUNTING: a sagging string of small pennants between adjacent masts,
    // strung from the mast HEADS so it arcs over the banners rather than
    // through them. Reuses the pennant materials, so it is free.
    //
    // MEASURED: the first pass reused the verge run's 1.5 × 0.95 PENNANT and
    // the flags read as white paper aeroplanes hanging in mid-air — bunting is
    // hand-sized, the verge pennant is a metre and a half of cloth on a 4 u
    // mast. BUNTING is a third of it.
    for (let m = 1; m < planted.length; m++) {
      const p0 = planted[m - 1];
      const p1 = planted[m];
      if (p1.along - p0.along > 14) continue; // a withheld mast breaks the run
      const N = 9;
      const cordY = (t: number): number => p0.y + 7.6 - 1.5 * Math.sin(Math.PI * t) + (p1.y - p0.y) * t;
      for (let k = 1; k < N; k++) {
        const t = k / N;
        b.add(
          pennantMats[flagIdx++ % pennantMats.length],
          BUNTING,
          p0.x + (p1.x - p0.x) * t,
          cordY(t) - 0.22,
          p0.z + (p1.z - p0.z) * t,
          p0.dir + (side < 0 ? Math.PI / 2 : -Math.PI / 2),
        );
      }
      // …and the CORD they hang from. Without it the flags read as loose paper
      // floating in mid-air (measured) — bunting is legible because the eye
      // follows the line, not the flags.
      for (let k = 0; k < N; k++) {
        const t0 = k / N;
        const t1 = (k + 1) / N;
        const mx = p0.x + (p1.x - p0.x) * (t0 + t1) / 2;
        const mz = p0.z + (p1.z - p0.z) * (t0 + t1) / 2;
        const seg = Math.hypot((p1.x - p0.x) / N, (p1.z - p0.z) / N, cordY(t1) - cordY(t0));
        const pitch = Math.atan2(cordY(t1) - cordY(t0), Math.hypot((p1.x - p0.x) / N, (p1.z - p0.z) / N));
        b.add(steel, BOX, mx, (cordY(t0) + cordY(t1)) / 2, mz, p0.dir, 0.05, 0.05, seg * 1.06, pitch);
      }
    }
  }

  // ---- Fire drums (the cold + desert endings) ------------------------------
  if (dress.braziers) {
    const ember = new THREE.MeshBasicMaterial({ color: 0xff8a2a });
    for (const side of sides) {
      for (let along = -46; along <= 34; along += 20) {
        const lat = hw + RAIL_CROWD.inner - 1.1; // between barrier and front row
        if (!dry(along, side, lat)) continue;
        const s = groundAt(along + rng.range(-2, 2));
        const x = s.x + s.rightX * side * lat;
        const z = s.z + s.rightZ * side * lat;
        const y = vergeY(s);
        b.add(steel, BOX, x, y + 0.55, z, s.dir, 0.76, 1.1, 0.76);
        b.add(ember, BOX, x, y + 1.14, z, s.dir, 0.62, 0.14, 0.62);
        b.add(ember, BOX, x, y + 1.34, z, s.dir + rng.range(-0.6, 0.6), 0.3, 0.42, 0.3);
      }
    }
  }

  // ---- Spectator flotilla moored off the water side ------------------------
  // Only for cities that already render boats, so it costs no draw call. The
  // craft sit OUTSIDE the shoreline by construction (that is where the water
  // is) — the inverse of every other test in this file.
  if (dress.flotilla) {
    for (const side of [-1, 1] as const) {
      for (let i = 0; i < dress.flotilla; i++) {
        const along = rng.range(-40, 40);
        const shore = shorelineAt(fin + along, side);
        const off = rng.range(7, 26);
        const rot = rng.range(-0.7, 0.7);
        const col = rng.pick(BOAT_COLORS);
        if (shore == null) continue;
        const s = groundAt(along);
        const lat = side * (shore + off);
        a.boats.push({
          x: s.x + s.rightX * lat,
          // MEASURED (down-ray onto the welded sheet, Lagos lagoon at the
          // finish): the water surface sits at road + 0.21 … 0.32, NOT at the
          // old ground grade — the first cut used road − 0.9 and sank the whole
          // flotilla out of sight. buildBoats lifts a hull 0.35 above the
          // instance, so −0.1 floats it on the waterline.
          y: s.y - 0.1,
          z: s.z + s.rightZ * lat,
          rotY: s.dir + rot,
          s: 1,
          color: col,
        });
      }
    }
  }

  b.build(group);
  group.matrixAutoUpdate = false;
  return group;
}
